import { useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { useNavigate } from "react-router"
import { ArrowLeft, ClockRewind } from "@untitledui/icons"
import { useOrganization } from "@/lib/organization/organization-context"
import { StepVehicle } from "./step-vehicle"
import { StepPhotos } from "./step-photos"
import { StepDamageMark } from "./step-damage-mark"
import { StepSend } from "./step-send"
import { useConsumerIntakeEstimateState } from "./use-consumer-intake-estimate-state"
import { RESUME_COPY } from "./copy"
import type { ShotKey } from "./types"

// Quick Estimate 2 — wizard orchestrator.
//
// Step model (4 logical steps, surfaced as 3 in the bottom rail
// because Damage-Mark is short and feels like a continuation of
// Photos):
//   0 — Vehicle (VIN scan or type)
//   1 — Photos (sequenced 4 sides + close-ups)
//   2 — Damage Mark (tap-and-mark on top-down vehicle)
//   3 — Send (review + submit)
//
// Resume: if the user landed with a hydrated draft, show a banner
// at the top giving them "Resume" or "Start over." Acknowledging
// the banner without resetting just hides it for this session.

type StepIndex = 0 | 1 | 2 | 3

export function ConsumerIntakeEstimateWizard() {
  const {
    state,
    actions,
    allRequiredCaptured,
    hydratedAt,
    acknowledgeResume,
  } = useConsumerIntakeEstimateState()
  const { activeTeamId } = useOrganization()
  const teamId = activeTeamId
  const navigate = useNavigate()

  // Initial step depends on the persisted draft state.
  const initialStep: StepIndex = !state.vehicle
    ? 0
    : allRequiredCaptured
      ? 3 // resume on review
      : 1 // resume on photos
  const [step, setStep] = useState<StepIndex>(initialStep)

  if (!teamId) {
    return (
      <div className="text-center py-12">
        <p className="text-[14px] text-zinc-500">
          You need to pick a team before starting an estimate.
        </p>
      </div>
    )
  }

  const handleVehicleContinue = () => setStep(1)
  const handlePhotosBack = () => setStep(0)
  const handlePhotosContinue = () => setStep(2)
  const handleDamageBack = () => setStep(1)
  const handleDamageContinue = () => setStep(3)
  const handleSendBack = () => setStep(2)

  // From the review screen, tapping (or long-pressing) a photo slot
  // jumps back to the photos step and clears that slot so the user
  // can retake. The photos screen's resume logic finds the first
  // empty slot automatically.
  const handlePhotoTap = (slotKey: ShotKey) => {
    actions.clearShot(slotKey)
    setStep(1)
  }

  // "Ship what I have" — early-exit during photos. Jump to the
  // damage-mark step so the user can still tap any panels they
  // know about; the send step is one more click after that.
  const handleShipNow = () => setStep(2)

  // Resume banner state — only show when there's something
  // resumable. A bare hydration with no vehicle and no shots is
  // not worth interrupting the user with.
  const hasResumableWork =
    state.vehicle != null || Object.keys(state.shots).length > 0
  const showResumeBanner = hydratedAt != null && hasResumableWork
  const minutesSinceDraft = hydratedAt
    ? Math.floor((Date.now() - hydratedAt) / 60000)
    : 0

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-zinc-50 to-white">
      <div aria-hidden className="pointer-events-none fixed inset-0">
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgb(228 228 231 / 0.55) 1px, transparent 0)",
            backgroundSize: "24px 24px",
          }}
        />
      </div>

      <div className="relative max-w-[640px] mx-auto px-4 md:px-8 py-6 md:py-10">
        {/* Top exit + Ship Now affordance */}
        <div className="flex items-center justify-between mb-6">
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="inline-flex items-center gap-1.5 h-9 -ml-1 pl-1.5 pr-3 rounded-lg text-[13px] font-medium text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100/70 transition-all duration-150"
          >
            <ArrowLeft className="size-3.5" aria-hidden />
            Leave for now
          </button>
          {/* Ship Now — only useful while in the photos step with at
              least one shot already captured. */}
          {step === 1 && Object.keys(state.shots).length >= 1 && (
            <button
              type="button"
              onClick={handleShipNow}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[12.5px] font-semibold text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 transition-all duration-150"
            >
              Ship what I have →
            </button>
          )}
        </div>

        {/* Resume banner — surfaces ONCE on first arrival when a draft
            was found. Fades out after the user picks an action. */}
        <AnimatePresence>
          {showResumeBanner && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
              className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4 flex items-start gap-3"
            >
              <div className="size-8 rounded-full bg-emerald-100 grid place-items-center shrink-0">
                <ClockRewind className="size-4 text-emerald-700" aria-hidden />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13.5px] font-semibold text-emerald-900 leading-tight">
                  {RESUME_COPY.title}
                </p>
                <p className="text-[12px] text-emerald-800/80 mt-0.5 leading-tight">
                  {RESUME_COPY.bodyMinutesAgo(minutesSinceDraft)}
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    actions.reset()
                    setStep(0)
                  }}
                  className="h-8 px-2.5 rounded-lg text-[12px] font-semibold text-emerald-700 hover:text-emerald-900 hover:bg-white/70 transition-colors duration-150"
                >
                  {RESUME_COPY.startOver}
                </button>
                <button
                  type="button"
                  onClick={acknowledgeResume}
                  className="h-8 px-3 rounded-lg text-[12.5px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition-colors duration-150"
                >
                  {RESUME_COPY.resume}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step card — single-column, focused. Steps render their own
            eyebrow / title / body inside; we own the padding here. */}
        <div className="rounded-3xl border border-zinc-200/70 bg-white shadow-2xl shadow-zinc-900/[0.07] overflow-hidden">
          <div className="p-7 md:p-10">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 18, scale: 0.985 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -18, scale: 0.985 }}
                transition={{
                  type: "spring",
                  stiffness: 260,
                  damping: 30,
                  mass: 0.9,
                  opacity: { duration: 0.25 },
                }}
              >
                {step === 0 && (
                  <StepVehicle
                    vin={state.vin}
                    vehicle={state.vehicle}
                    onVin={actions.setVin}
                    onVehicle={actions.setVehicle}
                    onContinue={handleVehicleContinue}
                  />
                )}
                {step === 1 && (
                  <StepPhotos
                    shots={state.shots}
                    closeUps={state.closeUps}
                    vehicle={state.vehicle}
                    actions={actions}
                    onBack={handlePhotosBack}
                    onContinue={handlePhotosContinue}
                  />
                )}
                {step === 2 && (
                  <StepDamageMark
                    selected={state.damageZones}
                    onToggle={actions.toggleDamageZone}
                    onBack={handleDamageBack}
                    onContinue={handleDamageContinue}
                  />
                )}
                {step === 3 && (
                  <StepSend
                    vehicle={state.vehicle}
                    shots={state.shots}
                    closeUps={state.closeUps}
                    notes={state.notes}
                    damageZones={state.damageZones}
                    customer={state.customer}
                    actions={actions}
                    teamId={teamId}
                    onBack={handleSendBack}
                    onPhotoTap={handlePhotoTap}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

      </div>
    </div>
  )
}
